import React, { useCallback, useEffect, useState } from 'react';
import { CalendarDays, CalendarPlus, CheckCircle2, ClipboardCheck, Download, Loader2, MapPin, Pencil, Trash2, Video, X, XCircle } from 'lucide-react';
import { adminApi } from '../../services/api';
import Fenetre from '../Fenetre';
import { Fiche } from '../MesDocuments';
import SelecteurActions from './SelecteurActions';
import {
  boutonPrincipal, boutonSecondaire, Carte, champ, Chargement, Erreur, fmtDateHeure, Pastille,
  STATUTS_SEANCE, TexteLignes, versChampDateHeure, Vide,
} from './ui';
import { annoncerErreur, annoncerSucces, confirmer, EtatErreur } from '../../etats';

const MODES = { visio: 'En ligne', presentiel: 'Présentiel', telephone: 'Téléphone' };

function libelleDuree(minutes) {
  if (minutes < 60) return `${minutes} min`;
  const reste = minutes % 60;
  return `${Math.floor(minutes / 60)} h${reste ? ` ${reste}` : ''}`;
}

function FormulaireSeance({ initial, actions, onAnnuler, onEnregistrer }) {
  const [v, setV] = useState(() => ({
    objet: initial?.objet || '',
    date_debut: versChampDateHeure(initial?.date_debut),
    duree_minutes: initial?.duree_minutes || 60,
    mode: initial?.mode || 'visio',
    lieu: initial?.lieu || '',
    lien_visio: initial?.lien_visio || '',
    ordre_du_jour: initial?.ordre_du_jour || '',
    action_ids: (initial?.actions || []).map((a) => a.id),
  }));
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState('');
  const maj = (cle) => (e) => setV((x) => ({ ...x, [cle]: e.target.value }));
  // Les actions encore ouvertes, plus celles déjà rattachées à la séance.
  const proposables = actions.filter((a) => a.statut !== 'termine' || v.action_ids.includes(a.id));

  const soumettre = async (e) => {
    e.preventDefault();
    if (!v.objet.trim() || !v.date_debut) { setErreur('Indiquez le sujet, la date et l’heure.'); return; }
    // Ce que l'organisation reçoit doit lui permettre de venir : un lien
    // pour une séance en ligne, un lieu pour une séance en présentiel.
    if (v.mode === 'visio' && !v.lien_visio.trim()) { setErreur('Une séance en ligne doit porter le lien de connexion.'); return; }
    if (v.mode === 'presentiel' && !v.lieu.trim()) { setErreur('Indiquez le lieu de la séance.'); return; }
    if (v.mode === 'telephone' && !v.lieu.trim()) { setErreur('Indiquez le numéro à appeler.'); return; }
    if (new Date(v.date_debut).getTime() <= Date.now()) { setErreur('La séance doit être programmée à une date à venir.'); return; }
    setEnvoi(true);
    setErreur('');
    try {
      await onEnregistrer({ ...v, duree_minutes: Number(v.duree_minutes), date_debut: new Date(v.date_debut).toISOString() });
      annoncerSucces(initial ? 'Séance mise à jour. L’association est prévenue.' : 'Séance proposée. L’association est prévenue par e-mail.');
    } catch (err) {
      setErreur(err.message);
      setEnvoi(false);
    }
  };

  return (
    <form onSubmit={soumettre} className="space-y-3">
      <label className="block text-xs font-semibold text-[var(--text-muted)]">Sujet *
        <input value={v.objet} onChange={maj('objet')} placeholder="Ex. Revue du plan d'actions — gouvernance" className={`${champ} mt-1`} />
      </label>
      <div className="grid sm:grid-cols-3 gap-3">
        <label className="block text-xs font-semibold text-[var(--text-muted)]">Date et heure *
          <input type="datetime-local" value={v.date_debut} onChange={maj('date_debut')} className={`${champ} mt-1`} />
        </label>
        <label className="block text-xs font-semibold text-[var(--text-muted)]">Durée
          <select value={v.duree_minutes} onChange={maj('duree_minutes')} className={`${champ} mt-1`}>
            {[30, 45, 60, 90, 120, 180, 240].map((m) => <option key={m} value={m}>{libelleDuree(m)}</option>)}
          </select>
        </label>
        <label className="block text-xs font-semibold text-[var(--text-muted)]">Mode
          <select value={v.mode} onChange={maj('mode')} className={`${champ} mt-1`}>
            {Object.entries(MODES).map(([cle, libelle]) => <option key={cle} value={cle}>{libelle}</option>)}
          </select>
        </label>
      </div>
      {v.mode === 'visio' ? (
        <label className="block text-xs font-semibold text-[var(--text-muted)]">Lien de connexion *
          <input type="url" required value={v.lien_visio} onChange={maj('lien_visio')} placeholder="https://" className={`${champ} mt-1`} />
        </label>
      ) : (
        <label className="block text-xs font-semibold text-[var(--text-muted)]">{v.mode === 'telephone' ? 'Numéro à appeler *' : 'Lieu *'}
          <input required value={v.lieu} onChange={maj('lieu')} className={`${champ} mt-1`} />
        </label>
      )}
      <label className="block text-xs font-semibold text-[var(--text-muted)]">Ordre du jour
        <textarea value={v.ordre_du_jour} onChange={maj('ordre_du_jour')} rows={3} className={`${champ} mt-1 resize-y`} />
      </label>
      {proposables.length > 0 && (
        <SelecteurActions actions={proposables} valeur={v.action_ids}
          onChange={(ids) => setV((x) => ({ ...x, action_ids: ids }))} />
      )}
      <Erreur message={erreur} />
      <div className="flex flex-wrap justify-end gap-2">
        <button type="button" onClick={onAnnuler} className={boutonSecondaire}><X className="w-4 h-4" />Annuler</button>
        <button type="submit" disabled={envoi} aria-busy={envoi || undefined} className={boutonPrincipal}>
          {envoi ? <Loader2 className="w-4 h-4 animate-spin" /> : <CalendarPlus className="w-4 h-4" />}
          {initial ? 'Reporter / enregistrer' : 'Proposer la séance'}
        </button>
      </div>
    </form>
  );
}

function CompteRendu({ seance, onEnregistrer, onAnnuler }) {
  const [texte, setTexte] = useState(seance.compte_rendu || '');
  const [envoi, setEnvoi] = useState(false);
  return (
    <div className="space-y-3">
      <p className="text-sm text-[var(--text-muted)]">{seance.objet} · {fmtDateHeure(seance.date_debut)}</p>
      <label className="block text-xs font-semibold text-[var(--text-muted)]">Compte rendu de séance
        <textarea value={texte} onChange={(e) => setTexte(e.target.value)} rows={4} className={`${champ} mt-1 resize-y`}
          placeholder="Points abordés, décisions, engagements de l'association…" />
      </label>
      <div className="flex flex-wrap justify-end gap-2">
        <button type="button" onClick={onAnnuler} className={boutonSecondaire}><X className="w-4 h-4" />Annuler</button>
        <button type="button" disabled={envoi} aria-busy={envoi || undefined} className={boutonPrincipal}
          onClick={async () => { setEnvoi(true); try { await onEnregistrer(texte); } finally { setEnvoi(false); } }}>
          {envoi ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}Marquer réalisée
        </button>
      </div>
    </div>
  );
}

/* Seances avec l'association : proposition, confirmation par l'association,
   invitation .ics, compte rendu. */
export default function SeancesProjet({ projetId, actions, peutAgir }) {
  const [seances, setSeances] = useState(null);
  const [erreur, setErreur] = useState(null);
  const [formulaire, setFormulaire] = useState(null); // null | 'nouvelle' | seance
  const [cloture, setCloture] = useState(null);
  // Le rapport de séance (la fiche de suivi), ouvert en fenêtre dès que la
  // séance est notée réalisée.
  const [fiche, setFiche] = useState(null);
  const [signaturePrete, setSignaturePrete] = useState(false);
  const [info, setInfo] = useState('');

  useEffect(() => {
    adminApi.maSignature().then((d) => setSignaturePrete(!!d.enregistree)).catch(() => {});
  }, []);

  const ouvrirLeRapport = async (seanceId) => {
    try {
      setFiche(await adminApi.ouvrirMaFiche(seanceId));
    } catch (e) {
      // Seul l'intervenant qui a animé la séance établit sa fiche.
      setInfo("Séance réalisée. Le rapport sera établi par l'intervenant qui l'a animée.");
    }
  };

  const charger = useCallback(() => {
    adminApi.getSeances(projetId)
      .then((s) => { setSeances(s); setErreur(null); })
      .catch((e) => setErreur(e));
  }, [projetId]);

  useEffect(() => { charger(); }, [charger]);

  const executer = async (promesse, succes) => {
    try {
      await promesse;
      if (succes) annoncerSucces(succes);
      charger();
    } catch (e) { annoncerErreur(e.message || "L'opération n'a pas abouti."); }
  };

  if (!seances) return erreur ? <EtatErreur erreur={erreur} onReessayer={charger} /> : <Chargement />;
  const maintenant = Date.now();
  const aVenir = seances
    .filter((s) => new Date(s.date_debut).getTime() >= maintenant && !['annulee', 'realisee'].includes(s.statut))
    .reverse();
  const passees = seances.filter((s) => !aVenir.includes(s));

  const rendu = (s) => {
    const statut = STATUTS_SEANCE[s.statut] || STATUTS_SEANCE.proposee;
    return (
      <li key={s.id} className="border border-[var(--border)] rounded-pastille p-4">
        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2 mb-1">
              <Pastille className={statut.className}>{statut.label}</Pastille>
              <span className="text-sm font-semibold text-[var(--text-bold)]">{fmtDateHeure(s.date_debut)}</span>
              <span className="text-xs text-[var(--text-muted)]">{libelleDuree(s.duree_minutes)}</span>
            </div>
            <p className="font-semibold text-[var(--text-bold)]">{s.objet}</p>
            <p className="text-xs text-[var(--text-muted)] mt-1 inline-flex items-center gap-1.5">
              {s.mode === 'visio' ? <Video className="w-3.5 h-3.5" /> : <MapPin className="w-3.5 h-3.5" />}
              {s.mode_libelle}{s.mode === 'visio' && s.lien_visio ? ' · ' : s.lieu ? ` · ${s.lieu}` : ''}
              {s.mode === 'visio' && s.lien_visio && <a href={s.lien_visio} target="_blank" rel="noreferrer" className="text-[var(--accent-texte)] font-semibold hover:underline">rejoindre</a>}
            </p>
          </div>
          <div className="flex flex-wrap gap-1 shrink-0">
            <button type="button" onClick={() => executer(adminApi.telechargerIcs(s.id))} aria-label="Ajouter à mon agenda (.ics)" title="Ajouter à mon agenda (.ics)"
              className="act act-icone act-tertiaire"><Download className="w-4 h-4" /></button>
            {peutAgir && !['annulee', 'realisee'].includes(s.statut) && (
              <>
                <button type="button" onClick={() => setFormulaire(s)} aria-label="Reporter ou modifier la séance" title="Reporter ou modifier"
                  className="act act-icone act-tertiaire"><Pencil className="w-4 h-4" /></button>
                <button type="button" onClick={() => setCloture(s)} aria-label="Compte rendu et clôture" title="Compte rendu"
                  className="act act-icone act-tertiaire"><CheckCircle2 className="w-4 h-4" /></button>
                <button type="button" aria-label="Annuler la séance" title="Annuler la séance"
                  onClick={async () => (await confirmer("Annuler cette séance ? L'association sera prévenue.")) && executer(adminApi.updateSeance(s.id, { statut: 'annulee' }), 'Séance annulée. L’association est prévenue.')}
                  className="act act-icone act-tertiaire"><XCircle className="w-4 h-4" /></button>
              </>
            )}
            {peutAgir && s.statut !== 'realisee' && (
              <button type="button" aria-label="Supprimer la séance" title="Supprimer"
                onClick={async () => (await confirmer('Supprimer définitivement cette séance ?')) && executer(adminApi.deleteSeance(s.id), 'Séance supprimée.')}
                className="act act-icone act-tertiaire"><Trash2 className="w-4 h-4" /></button>
            )}
          </div>
        </div>
        {s.ordre_du_jour && <TexteLignes texte={s.ordre_du_jour} className="text-sm text-[var(--text-main)] mt-2" />}
        {s.actions.length > 0 && (
          <ul className="mt-2 text-xs text-[var(--text-muted)] list-disc pl-5">{s.actions.map((a) => <li key={a.id}>{a.description}</li>)}</ul>
        )}
        {s.commentaire_association && (
          <p className="mt-2 text-sm bg-[var(--attention-fond)] text-[var(--attention)] rounded-pastille px-3 py-2">
            <span className="font-semibold">Réponse de l'association : </span>{s.commentaire_association}
          </p>
        )}
        {s.compte_rendu && <TexteLignes texte={`Compte rendu :\n${s.compte_rendu}`} className="mt-2 text-sm bg-[var(--bg-main)] rounded-pastille px-3 py-2 text-[var(--text-main)]" />}
      </li>
    );
  };

  return (
    <div className="space-y-4">
      {info && <p className="encart encart-info">{info}</p>}
      {fiche && (
        <Fenetre nu largeur="large" onClose={() => { setFiche(null); charger(); }}>
          <p className="text-white text-[13px] mb-2 px-1">
            Rapport de la séance — complétez-le pendant qu’elle est fraîche. Il sera transmis pour validation, puis facturable.
          </p>
          <Fiche fiche={fiche} signaturePrete={signaturePrete}
                 onFerme={() => { setFiche(null); charger(); }}
                 onChange={(maj) => setFiche(maj)} />
        </Fenetre>
      )}
      {formulaire && (
        <Fenetre largeur="large" icone={formulaire === 'nouvelle' ? CalendarPlus : Pencil}
          titre={formulaire === 'nouvelle' ? 'Programmer une séance' : 'Reporter ou modifier la séance'}
          onClose={() => setFormulaire(null)}>
          <FormulaireSeance initial={formulaire === 'nouvelle' ? null : formulaire} actions={actions}
            onAnnuler={() => setFormulaire(null)}
            onEnregistrer={async (d) => {
              if (formulaire === 'nouvelle') await adminApi.createSeance(projetId, d);
              else await adminApi.updateSeance(formulaire.id, d);
              setFormulaire(null);
              charger();
            }} />
        </Fenetre>
      )}
      {cloture && (
        <Fenetre icone={ClipboardCheck} titre="Compte rendu de séance" onClose={() => setCloture(null)}>
          <CompteRendu seance={cloture} onAnnuler={() => setCloture(null)} onEnregistrer={async (texte) => {
            try {
              await adminApi.updateSeance(cloture.id, { statut: 'realisee', compte_rendu: texte });
            } catch (e) { annoncerErreur(e.message || 'La séance n’a pas pu être clôturée.'); return; }
            annoncerSucces('Séance marquée réalisée.');
            const id = cloture.id;
            setCloture(null);
            charger();
            await ouvrirLeRapport(id);
          }} />
        </Fenetre>
      )}
      {peutAgir && (
        <Carte titre="Programmer une séance" action={(
          <button type="button" onClick={() => setFormulaire('nouvelle')} className={boutonPrincipal}><CalendarPlus className="w-4 h-4" />Nouvelle séance</button>
        )}>
          <p className="text-sm text-[var(--text-muted)]">L'association reçoit la proposition par e-mail, la confirme ou demande un autre créneau depuis son espace. Un rappel part la veille.</p>
        </Carte>
      )}
      <Carte titre={`À venir (${aVenir.length})`}>
        {aVenir.length === 0 ? <Vide icone={CalendarDays} titre="Aucune séance à venir" /> : <ul className="space-y-3">{aVenir.map(rendu)}</ul>}
      </Carte>
      {passees.length > 0 && (
        <Carte titre={`Historique (${passees.length})`}>
          <ul className="space-y-3">{passees.map(rendu)}</ul>
        </Carte>
      )}
    </div>
  );
}